import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, TrendingUp, DollarSign } from 'lucide-react';
import { useMedQueue, useMedStats, MedRequest, getStatusLabel, getStatusColor, getReasonLabel, getPriorityLabel, getPriorityColor } from '../../../hooks/useMed';

const statusBadgeClasses: Record<string, string> = {
  yellow: 'bg-yellow-500/20 text-yellow-400',
  blue: 'bg-blue-500/20 text-blue-400',
  green: 'bg-green-500/20 text-green-400',
  red: 'bg-red-500/20 text-red-400',
  gray: 'bg-surface-600 text-surface-300',
};

const priorityBadgeClasses: Record<string, string> = {
  gray: 'bg-surface-600 text-surface-300',
  blue: 'bg-blue-500/20 text-blue-400',
  orange: 'bg-orange-500/20 text-orange-400',
  red: 'bg-red-500/20 text-red-400',
};

export function MedQueue() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ status: 'PENDING', priority: '', page: 1, limit: 20 });
  const { data, isLoading } = useMedQueue(filters);
  const { data: stats } = useMedStats();

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');
  const formatCurrency = (cents: number) => (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const StatusBadge = ({ status }: { status: string }) => {
    const color = getStatusColor(status);
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadgeClasses[color] || statusBadgeClasses.gray}`}>{getStatusLabel(status)}</span>;
  };

  const PriorityBadge = ({ priority }: { priority: string }) => {
    const color = getPriorityColor(priority);
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityBadgeClasses[color] || priorityBadgeClasses.gray}`}>{getPriorityLabel(priority)}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Fila MED - Compliance</h1>
          <p className="text-surface-400">Solicitações de devolução PIX pendentes de análise</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg"><Clock className="w-5 h-5 text-yellow-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-surface-400">Pendentes</p>
              </div>
            </div>
          </div>
          <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg"><AlertTriangle className="w-5 h-5 text-blue-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.underReview}</p>
                <p className="text-sm text-surface-400">Em Análise</p>
              </div>
            </div>
          </div>
          <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg"><DollarSign className="w-5 h-5 text-green-400" /></div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalRefundedAmount)}</p>
                <p className="text-sm text-surface-400">Total Devolvido</p>
              </div>
            </div>
          </div>
          <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg"><TrendingUp className="w-5 h-5 text-purple-400" /></div>
              <div>
                <p className="text-2xl font-bold">{stats.avgProcessingDays?.toFixed(1) || 0}d</p>
                <p className="text-sm text-surface-400">Tempo Médio</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-surface-900 rounded-xl p-4 border border-surface-800">
        <div className="flex flex-wrap gap-4 items-end">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="w-44 px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os status</option>
            <option value="PENDING">Pendente</option>
            <option value="UNDER_REVIEW">Em Análise</option>
          </select>
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value, page: 1 })}
            className="w-44 px-3 py-2 bg-surface-700 border border-surface-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todas as prioridades</option>
            <option value="CRITICAL">Crítica</option>
            <option value="HIGH">Alta</option>
            <option value="MEDIUM">Média</option>
            <option value="LOW">Baixa</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data?.data?.length ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-surface-600" />
            <p className="text-surface-400">Nenhuma solicitação na fila</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Solicitante</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Motivo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Valor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Prioridade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-800">
              {data.data.map((item: MedRequest) => (
                <tr key={item.id} className="hover:bg-surface-800/50 transition-colors">
                  <td className="px-6 py-4 text-sm">{formatDate(item.createdAt)}</td>
                  <td className="px-6 py-4 text-sm">{item.userName}</td>
                  <td className="px-6 py-4 text-sm text-surface-300">{getReasonLabel(item.reason)}</td>
                  <td className="px-6 py-4 font-medium">{formatCurrency(item.requestedAmount)}</td>
                  <td className="px-6 py-4"><PriorityBadge priority={item.priority} /></td>
                  <td className="px-6 py-4"><StatusBadge status={item.status} /></td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => navigate(`/admin/compliance/med/${item.id}`)}
                      className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm transition-colors"
                    >
                      Analisar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default MedQueue;
